import BookingItemEntity from '../entities/BookingItem';
import { Company } from '../entities/Company';
import { CurrencyConversion } from './currencies';
import { getExchangeRate } from './rates';

export const PLATFORM_FEE_PERCENT = 5;
export const DEFAULT_COMMISSION_FEE_PERCENT = 11;
export const DEFAULT_FEES = [DEFAULT_COMMISSION_FEE_PERCENT, PLATFORM_FEE_PERCENT];

interface PriceInCurrencyParams {
  value: number;
  currencyConversion: CurrencyConversion;
  fractionDigits?: number;
}

type GetUnitAmountInclCommissionFeeParams = Pick<BookingItemEntity, 'unitAmount' | 'taxAmount' | 'quantity' | 'recoverableTax' | 'commissionFeePercent'>;

type GetPartnerUnitAmountInclTax = Pick<BookingItemEntity, 'unitAmount' | 'taxAmount' | 'quantity' | 'recoverableTax'>;

export interface AmountConverted {
  value: number;
  rate: number;
}

const locale = typeof window !== 'undefined' && window.navigator ? window.navigator.language : 'en-US';

export function formatPrice(value: number, currency: string, fractionDigits = 2) {
  const options: Intl.NumberFormatOptions = {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  };

  return value.toLocaleString(locale, {
    style: 'currency',
    currency,
    ...options
  }).replace(' ', ' ');
}

export function getCurrencyChar(currency: string) {
  const intl = new Intl.NumberFormat(locale, { style: 'currency', currency });
  const match = intl.formatToParts(1).find(({ type }) => type === 'currency');

  return match ? match.value : '€';
}

export function getAmountInclFee(amount: number, feePercent: number): number {
  return amount / (1 - (feePercent / 100));
}

export function getPriceInCurrency(value: number, currencyConversion: CurrencyConversion): AmountConverted {
  const exchangeRate = getExchangeRate(currencyConversion);

  return {
    value: value * exchangeRate,
    rate: exchangeRate
  };
}

export function getPartnerUnitTaxAmount({ taxAmount, quantity }: Pick<BookingItemEntity, 'taxAmount' | 'quantity'>): number {
  return taxAmount / quantity;
}

export function getPartnerUnitAmountInclTax(params: GetPartnerUnitAmountInclTax): number {
  const { unitAmount, taxAmount, quantity, recoverableTax } = params;

  const unitTaxAmount = getPartnerUnitTaxAmount({ taxAmount, quantity });
  const unitAmountInclTax = unitAmount + (recoverableTax ? 0 : unitTaxAmount);

  return unitAmountInclTax;
}

export function getUnitAmountInclCommissionFee({ commissionFeePercent, ...params }: GetUnitAmountInclCommissionFeeParams): number {
  const partnerUnitAmountInclTax = getPartnerUnitAmountInclTax(params);
  const amount = getAmountInclFee(partnerUnitAmountInclTax, commissionFeePercent);

  return roundAmount(amount);
}

export function formatPriceInCurrency({ value, currencyConversion, fractionDigits }: PriceInCurrencyParams) {
  const { value: convertedValue } = getPriceInCurrency(value, currencyConversion);

  return formatPrice(convertedValue, currencyConversion.target, fractionDigits);
}

export function formatPercentage(value: number, fractionDigits = 1): string {
  const percentage = Number.isInteger(value) ? value : parseFloat(value.toFixed(fractionDigits));

  return `${percentage}%`;
}

export function formatConversionRate(source: string, target: string, rate: number): string {
  return source === target ? '' : `1 ${source} = ${rate.toFixed(3)} ${target}`;
}

export function roundAmount(value: number): number {
  return Math.round(value * 100) / 100;
}

export function getExperienceFees(company: Pick<Company, 'platformFeePercent'>): number[] {
  if (typeof company.platformFeePercent === 'number') {
    return [company.platformFeePercent];
  }

  return DEFAULT_FEES;
}